import { useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { SERVICES, FEATURES, BENEFITS, scrollToId, openInquiry, slug, EASE } from "@/lib/site";
import { PrimaryButton, Arrow, OutlineButton } from "@/components/site/Buttons";
import { LineReveal, Reveal, ClipImage } from "@/components/site/motion";

const PANEL_THEMES = [
  { bg: "bg-white border border-charcoal/10", text: "text-charcoal", sub: "text-charcoal/70", mono: "text-leaf", rule: "bg-charcoal/20" },
  { bg: "texture-forest", text: "text-cream", sub: "text-cream/85", mono: "text-sun", rule: "bg-cream/30" },
  { bg: "bg-charcoal", text: "text-cream", sub: "text-cream/80", mono: "text-sun", rule: "bg-cream/25" },
];

const ServicePanel = ({ service, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.93]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);
  const th = PANEL_THEMES[index % PANEL_THEMES.length];
  return (
    <motion.div
      ref={ref}
      data-testid={`service-panel-${index + 1}`}
      style={{ scale, opacity, top: `${92 + 14 * index}px` }}
      className={`sticky mb-8 sm:mb-10 rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden ${th.bg} will-change-transform`}
    >
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center p-6 sm:p-12 lg:p-16 min-h-[68vh]">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-4 mb-7">
            <span className={`font-mono text-sm ${th.mono}`}>{service.num}</span>
            <span className={`h-px w-10 ${th.rule}`} />
            <span className={`font-mono text-[11px] sm:text-xs tracking-[0.24em] ${th.mono}`}>{service.name}</span>
          </div>
          <h3 className={`font-display font-medium tracking-[-0.02em] leading-[1.05] text-[1.9rem] sm:text-5xl lg:text-[3.4rem] ${th.text}`}>{service.title}</h3>
          {service.copy.map((p, i) => (
            <p key={i} className={`mt-5 text-sm sm:text-base leading-relaxed max-w-xl ${th.sub}`}>{p}</p>
          ))}
          <PrimaryButton testid={`service-cta-${index + 1}`} onClick={() => openInquiry(service.inquiry)} className="mt-9">
            {service.cta} <Arrow />
          </PrimaryButton>
        </div>
        <div className="lg:col-span-6">
          <div className="group overflow-hidden rounded-2xl sm:rounded-[1.75rem] aspect-[16/11] lg:aspect-[16/12]">
            <img src={service.image.src} srcSet={service.image.srcSet} sizes="(max-width: 1024px) 100vw, 45vw" alt={service.image.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const Services = () => (
  <section id="solutions" data-testid="services-section" className="px-3 sm:px-5 lg:px-8 py-16 sm:py-32">
    <div className="px-3 sm:px-5 lg:px-8 max-w-[1700px] mx-auto">
      <p className="eyebrow mb-5">OUR SERVICES</p>
      <LineReveal
        as="h2"
        className="font-display font-medium tracking-[-0.03em] leading-[1.05] text-4xl sm:text-6xl lg:text-[4.4rem] text-charcoal max-w-5xl"
        lines={[<>Your ideas. <span className="font-serif italic font-normal">A plan for your yard.</span></>]}
      />
      <Reveal delay={0.15}>
        <p className="mt-5 text-base sm:text-lg text-charcoal/75 max-w-2xl">Start with the service closest to the change you have in mind.</p>
      </Reveal>
      <div className="mb-14 lg:mb-20" />
    </div>
    <div className="relative max-w-[1700px] mx-auto">
      {SERVICES.map((s, i) => <ServicePanel key={s.num} service={s} index={i} />)}
    </div>
    <Reveal y={20} className="mt-14 flex flex-wrap items-center justify-center gap-5 max-w-[1700px] mx-auto">
      <PrimaryButton testid="services-send-inquiry-button" onClick={() => openInquiry()}>DISCUSS MY PROJECT <Arrow /></PrimaryButton>
      <OutlineButton testid="services-explore-features-button" onClick={() => scrollToId("capabilities")}>EXPLORE ALL FEATURES</OutlineButton>
    </Reveal>
  </section>
);

const LAYOUTS = [
  { img: "lg:col-span-8", txt: "lg:col-span-4", aspect: "aspect-[16/10]", overlap: "lg:pl-4 lg:pb-10" },
  { img: "lg:col-span-7 lg:col-start-6", txt: "lg:col-span-4 lg:col-start-1 lg:row-start-1", aspect: "aspect-[4/3]", overlap: "lg:-mr-24 lg:pb-12 lg:text-right lg:items-end" },
  { img: "lg:col-span-8 lg:col-start-3", txt: "lg:col-span-4 lg:col-start-1 lg:row-start-1", aspect: "aspect-[21/11]", overlap: "lg:pt-10" },
];

const FeatureCard = ({ feature, index, onSelect }) => {
  const L = LAYOUTS[index % LAYOUTS.length];
  return (
    <Reveal y={50}>
      <div
        data-testid={`capability-card-${slug(feature.title)}`}
        onClick={() => onSelect(index)}
        role="button"
        tabIndex={0}
        aria-label={`View ${feature.title} details`}
        onKeyDown={(e) => e.key === "Enter" && onSelect(index)}
        className="group grid lg:grid-cols-12 gap-6 items-end cursor-pointer rounded-[1.75rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf focus-visible:ring-offset-4 focus-visible:ring-offset-cream"
      >
        <div className={`${L.img} relative`}>
          <ClipImage image={feature.image} className={`${L.aspect} w-full`} parallax={6} rounded="rounded-[1.75rem]" imgClassName="transition-transform duration-[1300ms] ease-out group-hover:scale-[1.2]" sizes="(max-width: 1024px) 100vw, 60vw" />
          <span className="absolute top-5 left-5 font-mono text-[11px] tracking-[0.22em] text-cream bg-charcoal/50 backdrop-blur px-3 py-1.5 rounded-full">{String(index + 1).padStart(2, "0")}</span>
        </div>
        <div className={`${L.txt} relative z-10 flex flex-col ${L.overlap}`}>
          <p className="eyebrow mb-3">{feature.tag}</p>
          <h3 className="font-display font-medium tracking-[-0.02em] text-2xl sm:text-4xl lg:text-[2.9rem] leading-[1.04] text-charcoal transition-transform duration-500 group-hover:translate-x-2 break-words">{feature.title}</h3>
          <p className="mt-4 text-sm sm:text-base text-charcoal/70 leading-relaxed max-w-sm">{feature.copy}</p>
          <span className="mt-6 inline-flex w-12 h-12 rounded-full border border-leaf/40 items-center justify-center text-leaf transition-[background-color,border-color,color,transform] duration-500 group-hover:bg-sun group-hover:border-sun group-hover:text-charcoal group-hover:rotate-45">
            <ArrowUpRight className="w-5 h-5" />
          </span>
        </div>
      </div>
    </Reveal>
  );
};

const FeatureOverlay = ({ index, onClose }) => {
  const f = FEATURES[index];
  return (
    <motion.div data-testid="capability-overlay" className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} role="dialog" aria-modal="true" aria-label={f.title}>
      <div className="absolute inset-0 bg-charcoal/60 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="relative w-full max-w-4xl bg-cream rounded-[2rem] overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
        data-lenis-prevent
      >
        <div className="relative aspect-[16/10] sm:aspect-[16/9] overflow-hidden">
          <img src={f.image.src} srcSet={f.image.srcSet} sizes="(max-width: 900px) 100vw, 900px" alt={f.image.alt} className="absolute inset-0 w-full h-full object-cover" />
          <button data-testid="capability-overlay-close" onClick={onClose} aria-label="Close project details" className="absolute top-4 right-4 w-10 h-10 rounded-full bg-cream/90 backdrop-blur flex items-center justify-center text-charcoal hover:bg-sun transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 sm:p-10 flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-md">
            <p className="eyebrow mb-2">{String(index + 1).padStart(2, "0")} — {f.tag}</p>
            <h3 className="font-display font-medium tracking-[-0.02em] text-2xl sm:text-3xl text-charcoal">{f.title}</h3>
            <p className="mt-2 text-sm text-charcoal/70 leading-relaxed">{f.copy}</p>
          </div>
          <PrimaryButton testid="capability-overlay-inquiry-button" onClick={() => { onClose(); openInquiry(f.inquiry); }}>
            DISCUSS THIS PROJECT <Arrow />
          </PrimaryButton>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Features = () => {
  const [selected, setSelected] = useState(null);
  return (
    <section id="capabilities" data-testid="capabilities-section" className="px-6 sm:px-10 lg:px-16 py-16 sm:py-36 max-w-[1700px] mx-auto">
      <div className="grid lg:grid-cols-12 gap-8 items-end mb-16 lg:mb-28">
        <div className="lg:col-span-8">
          <p className="eyebrow mb-5">EXPLORE THE POSSIBILITIES</p>
          <LineReveal
            as="h2"
            className="font-display font-medium tracking-[-0.03em] leading-[1.05] text-3xl sm:text-5xl lg:text-[4.2rem] text-charcoal"
            lines={[<>More ways to enjoy <span className="font-serif italic font-normal">the space you already have.</span></>]}
          />
        </div>
        <Reveal className="lg:col-span-4 lg:pb-3" delay={0.15}>
          <p className="text-sm sm:text-base text-charcoal/70 leading-relaxed">
            Think about the change you would notice most: somewhere to gather, a garden you love looking at, or planting chosen with water use in mind.
          </p>
        </Reveal>
      </div>
      <div className="space-y-20 lg:space-y-36">
        {FEATURES.map((f, i) => <FeatureCard key={f.title} feature={f} index={i} onSelect={setSelected} />)}
      </div>
      <Reveal y={20} className="mt-20 lg:mt-28 flex flex-wrap items-center justify-center gap-4">
        <OutlineButton testid="view-all-capabilities-button" onClick={() => scrollToId("range")}>EXPLORE ALL FEATURES</OutlineButton>
        <PrimaryButton testid="capabilities-send-inquiry-button" onClick={() => openInquiry()}>DISCUSS MY PROJECT <Arrow /></PrimaryButton>
      </Reveal>
      <AnimatePresence>{selected !== null && <FeatureOverlay index={selected} onClose={() => setSelected(null)} />}</AnimatePresence>
    </section>
  );
};

const BenefitRow = ({ item, i }) => (
  <motion.div
    data-testid={`why-item-${i + 1}`}
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, ease: EASE }}
    className="group border-t border-charcoal/10 py-8 sm:py-10 grid sm:grid-cols-12 gap-3 sm:gap-6 items-baseline hover:bg-leaf/[0.04] transition-colors duration-500 px-2 sm:px-4 -mx-2 sm:-mx-4 rounded-lg"
  >
    <span className="sm:col-span-2 font-mono text-xs text-leaf">{String(i + 1).padStart(2, "0")}</span>
    <div className="sm:col-span-4 transition-transform duration-500 group-hover:translate-x-2">
      <p className="font-mono text-[11px] tracking-[0.2em] text-leaf mb-2">{item.label}</p>
      <h3 className="font-display font-bold tracking-tight text-xl sm:text-2xl text-charcoal">{item.title}</h3>
    </div>
    <p className="sm:col-span-6 text-sm sm:text-base text-charcoal/70 leading-relaxed max-w-xl">{item.copy}</p>
  </motion.div>
);

export const Benefits = () => (
  <section data-testid="benefits-section" className="px-6 sm:px-10 lg:px-16 py-16 sm:py-32 max-w-[1700px] mx-auto">
    <p className="eyebrow mb-5">WHAT MATTERS IN YOUR NEXT YARD</p>
    <LineReveal
      as="h2"
      className="font-display font-medium tracking-[-0.03em] leading-[1.05] text-3xl sm:text-5xl lg:text-[4rem] text-charcoal max-w-5xl"
      lines={[<>Start with the space. <span className="font-serif italic font-normal">Design around your life.</span></>]}
    />
    <Reveal delay={0.1} className="mt-8 mb-14 lg:mb-20 grid lg:grid-cols-2 gap-5 lg:gap-12 max-w-5xl">
      <p className="text-sm sm:text-base text-charcoal/70 leading-relaxed">What would make your yard feel better to use? Think about where you relax, the views you enjoy, the routes you take, and how much gardening you want to do.</p>
      <p className="text-sm sm:text-base text-charcoal/70 leading-relaxed">Your priorities help shape the conversation about layout, planting, materials, and irrigation. Tell us what matters most before you settle on individual features.</p>
    </Reveal>
    <div className="border-b border-charcoal/10">
      {BENEFITS.map((b, i) => <BenefitRow key={b.label} item={b} i={i} />)}
    </div>
    <Reveal y={20} className="mt-14 flex justify-center">
      <PrimaryButton testid="why-tell-us-button" onClick={() => openInquiry()}>TELL US ABOUT YOUR YARD <Arrow /></PrimaryButton>
    </Reveal>
  </section>
);
